import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  ArrowRight,
  ChevronRight,
  Phone,
  Stethoscope,
  Scale,
  Building2,
  Calculator,
  Briefcase,
} from "lucide-react";
import { industries, companyInfo } from "../data/mock";
import { useScrollReveal } from "../hooks/useScrollReveal";

const iconMap = { Stethoscope, Scale, Building2, Calculator, Briefcase };

const Section = ({ children, className = "" }) => {
  const [ref, isVisible] = useScrollReveal();
  return (
    <section
      ref={ref}
      className={`reveal ${isVisible ? "visible" : ""} ${className}`}
    >
      {children}
    </section>
  );
};

export default function Industries() {
  return (
    <div>
      {/* Hero */}
      <section className="bg-slate-50 py-16 lg:py-20">
        <div className="container mx-auto px-6 page-enter">
          <div className="max-w-3xl">
            <Badge
              variant="secondary"
              className="mb-4 px-3 py-1 text-blue-700 bg-blue-50 border border-blue-100 text-xs font-medium uppercase tracking-wider"
            >
              Industries We Serve
            </Badge>
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-4">
              IT Support Tailored to Your Industry in Australia
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Every industry has unique technology requirements — from
              compliance and data security to uptime and software integrations.
              Our deep industry experience means we understand your challenges
              from day one, not month one.
            </p>
          </div>
        </div>
      </section>

      {/* Industries List */}
      <Section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="space-y-6">
            {industries.map((ind, i) => {
              const Icon = iconMap[ind.icon] || Briefcase;
              return (
                <Link key={ind.id} to={`/industries/${ind.id}`}>
                  <Card className="service-card border-slate-100 hover:border-blue-100 bg-white group cursor-pointer mb-6">
                    <CardContent className="p-8 lg:p-10">
                      <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                        <div className="w-14 h-14 rounded-xl bg-blue-50 flex items-center justify-center shrink-0 group-hover:bg-blue-100 transition-colors duration-300">
                          <Icon className="w-7 h-7 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <h2 className="text-xl font-semibold text-slate-900 mb-2">
                            {ind.title}
                          </h2>
                          <p className="text-slate-600 leading-relaxed">
                            {ind.shortDesc}
                          </p>
                        </div>
                        <div className="shrink-0">
                          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 group-hover:gap-2.5 transition-all duration-200">
                            Learn more
                            <ChevronRight className="w-4 h-4" />
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </Section>

      {/* CTA */}
      <Section className="py-16 lg:py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-slate-900 tracking-tight mb-4">
              Don't See Your Industry?
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              We work with businesses across many sectors. If you don't see your
              industry listed, get in touch — we'd love to learn about your IT
              needs.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                asChild
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white btn-primary h-12 px-8"
              >
                <Link to="/contact">
                  Contact Us
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="h-12 px-8 border-slate-300 text-slate-700"
              >
                <a href={`tel:${companyInfo.phone.replace(/\s/g, "")}`}>
                  <Phone className="w-4 h-4 mr-2" />
                  {companyInfo.phone}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}
